import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { formatApiError } from "@/api";

export default function AdminLogin() {
  const { admin, loginPassword } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (admin) navigate("/admin", { replace: true });
  }, [admin, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginPassword(email, password);
      toast.success("Welcome back");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-void flex items-center justify-center px-6 timeline-grid">
      <div className="noise-overlay" />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <div className="flex items-center gap-3 mb-8">
          <span className="h-2 w-2 rounded-full bg-navy-ink animate-pulse" />
          <span className="font-mono text-xs tracking-[0.3em] uppercase text-bone/60">Studio Admin</span>
        </div>
        <h1 className="font-display text-5xl font-semibold uppercase tighter leading-none text-bone">
          Sign in
        </h1>
        <p className="mt-4 font-body font-light text-bone/50">
          Manage your projects and messages.
        </p>

        <form onSubmit={submit} className="mt-10 space-y-6" data-testid="admin-login-form">
          <div className="border-b border-border py-3 focus-within:border-navy-ink transition-colors">
            <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-bone/40 mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="admin-email"
              className="w-full bg-transparent font-body text-lg text-bone outline-none"
            />
          </div>
          <div className="border-b border-border py-3 focus-within:border-navy-ink transition-colors">
            <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-bone/40 mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="admin-password"
              className="w-full bg-transparent font-body text-lg text-bone outline-none"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full rounded-full bg-navy py-4 font-mono text-xs tracking-[0.2em] uppercase text-white hover:bg-navy-ink transition-colors disabled:opacity-50"
          >
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>

        <div className="my-8 flex items-center gap-4">
          <span className="h-px flex-1 bg-border" />
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-bone/30">or</span>
          <span className="h-px flex-1 bg-border" />
        </div>

        <button
          onClick={googleLogin}
          data-testid="admin-google-login"
          className="w-full rounded-full border border-border py-4 font-mono text-xs tracking-[0.2em] uppercase text-bone hover:border-bone/50 transition-colors"
        >
          Continue with Google
        </button>

        <a
          href="/"
          className="mt-8 block text-center font-mono text-[10px] tracking-[0.2em] uppercase text-bone/40 hover:text-navy-ink transition-colors"
        >
          ← Back to site
        </a>
      </motion.div>
    </div>
  );
}
